import { useKeycloak } from 'all-in-one-react-keycloak';
import { Benefit, useDiagnosisSearch } from 'all-in-one-shared-api';
import { Select, Spin } from 'antd';
import { FormSelect, FormWrapper } from 'components/form';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NewClaimUtils } from '../../utils/newClaimUtils';
import { ClaimInfoCardUtils } from './utils';

interface DiagnosisSelectProps {
  selectedClaimTypeObject: Benefit[];
}

export const DiagnosisSelect = ({ selectedClaimTypeObject }: DiagnosisSelectProps) => {
  const { t, i18n } = useTranslation();
  const { initialized, keycloak } = useKeycloak();
  const { getValues, setValue, watch } = useFormContext();

  const patientMemberType = getValues('patientMemberType');
  const claimType = getValues('claimType');
  const diagnosisCode = watch('diagnosisCode');
  const diagnosisName = watch('diagnosisName');
  const claims = getValues('claims');

  const params = useMemo(() => {
    if (!patientMemberType || !selectedClaimTypeObject)
      return {
        claimTypes: '',
        benefitCodes: [''],
      };
    return NewClaimUtils.mapViewClaimFilter(patientMemberType, {
      type: claimType,
      data: selectedClaimTypeObject,
      requiredDocs: [],
    });
  }, [selectedClaimTypeObject, patientMemberType, claimType]);
  const { data, shouldFetch, isLoading, mutate } = useDiagnosisSearch(
    { name: `diagnosis`, params },
    keycloak.token || '',
    '',
    ...(params.claimTypes || ''),
    params.benefitCodes,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  const currentClaimsString = useMemo(() => (claims || []).map((claim: Benefit) => claim.code).toString(), [claims]);

  useEffect(() => {
    // 当页面重新进入时，强制重新获取数据以更新缓存
    if (initialized && !!keycloak.authenticated && !!keycloak.token) {
      shouldFetch(true);
      // 强制重新验证数据，确保获取最新数据
      mutate();
    }
  }, [initialized, keycloak.authenticated, keycloak.token, shouldFetch, mutate]);

  useEffect(() => {
    if (!data?.results || !diagnosisCode) return;

    // const diagnosis = data?.results.find((o) => o.code === diagnosisCode);
    // if (!diagnosis) setValue('diagnosisCode', '');
    // setValue('diagnosisName', diagnosis);
  }, [data?.results, diagnosisCode, i18n.language, setValue]);

  return (
    <FormWrapper name="diagnosisCode" label={t('newClaim.claimInfo.diagnosisLabel')}>
      <FormSelect
        name="diagnosisCode"
        allowClear
        showSearch
        notFoundContent={isLoading ? <Spin size="small" /> : null}
        filterOption={(inputValue, option) => {
          if (!option?.label) return false;

          return String(option?.label)?.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0;
        }}
        placeholder={t('newClaim.claimInfo.diagnosisPlaceholder')}
        disabled={currentClaimsString === ''}
      >
        {(() => {
          let diagnosticOptions: Array<{
            code: string;
            label: string;
            description?: string;
            chineseDescription?: string;
          }> = [];

          if (data) {
            const formattedData = ClaimInfoCardUtils.formatDiagnosticData(data, i18n.language) || [];
            const isCurrentDiagnosisInData = data?.results.find((o) => o.code === diagnosisCode);

            if (isCurrentDiagnosisInData) {
              diagnosticOptions = formattedData;
            } else {
              diagnosticOptions = [...formattedData];
              if (diagnosisName) {
                diagnosticOptions.push({
                  ...diagnosisName,
                  label: i18n.language === 'en' ? diagnosisName.description : diagnosisName.chineseDescription,
                });
              }
            }
          } else if (diagnosisName) {
            diagnosticOptions = [
              {
                ...diagnosisName,
                label: i18n.language === 'en' ? diagnosisName.description : diagnosisName.chineseDescription,
              },
            ];
          }

          return diagnosticOptions.map((diagnosis) => (
            <Select.Option key={diagnosis.code} value={diagnosis.code} label={diagnosis.label}>
              {diagnosis.label}
            </Select.Option>
          ));
        })()}
      </FormSelect>
    </FormWrapper>
  );
};
